import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatLifetime, CONSENT_CATEGORIES, type CookieCategory } from '@ilc/shared';
import {
  api,
  ApiError,
  type CookieRow,
  type ScanRow,
  type SiteDetail,
  type StatsResponse,
} from '../lib/api';
import {
  Alert,
  Button,
  Card,
  CategoryBadge,
  EmptyState,
  Field,
  SectionTitle,
  Select,
  Spinner,
  StatTile,
  Toggle,
  cx,
} from '../components/ui';
import { useEnter, useStagger } from '../lib/motion';
import { SettingsTab } from './SiteSettingsTab';
import { EmbedTab } from './SiteEmbedTab';

type Tab = 'overview' | 'cookies' | 'banner' | 'embed' | 'settings';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'overview', label: 'Übersicht' },
  { id: 'cookies', label: 'Cookies' },
  { id: 'banner', label: 'Banner' },
  { id: 'embed', label: 'Einbindung' },
  { id: 'settings', label: 'Einstellungen' },
];

const CATEGORY_LABELS: Record<string, string> = {
  necessary: 'Notwendig',
  preferences: 'Präferenzen',
  statistics: 'Statistik',
  marketing: 'Marketing',
  unclassified: 'Ungeklaert',
};

export function SiteDetailPage() {
  const { siteId = '' } = useParams();
  const [site, setSite] = useState<SiteDetail | null>(null);
  const [snippet, setSnippet] = useState('');
  const [scannerDa, setScannerDa] = useState(true);
  const [tab, setTab] = useState<Tab>('overview');
  const [error, setError] = useState<string | null>(null);
  const contentRef = useEnter<HTMLDivElement>([tab]);

  const load = useCallback(async () => {
    try {
      const data = await api.site(siteId);
      setSite(data.site);
      setSnippet(data.embedSnippet);
      setScannerDa(data.scannerConfigured);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Laden fehlgeschlagen.');
    }
  }, [siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Alert tone="error">{error}</Alert>;
  if (!site)
    return (
      <div className="flex justify-center py-16 text-[var(--text-muted)]">
        <Spinner />
      </div>
    );

  return (
    <div>
      <Link to="/sites" className="mb-4 inline-flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text)]">
        <span aria-hidden="true">&larr;</span> Alle Websites
      </Link>

      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{site.name}</h1>
          <p className="tabular text-sm text-[var(--text-muted)]">{site.domain}</p>
        </div>
        <span className="tabular rounded-lg bg-[var(--surface)] px-3 py-1.5 text-xs text-[var(--text-muted)] ring-1 ring-[var(--border)]">
          Einwilligungsversion {site.consentVersion}
        </span>
      </div>

      {/* Tabs: horizontal scrollbar auf dem Handy, sonst nebeneinander */}
      <div role="tablist" className="mb-5 flex gap-1 overflow-x-auto border-b border-[var(--border)] pb-px">
        {TABS.map((item) => (
          <button
            key={item.id}
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            className={cx(
              'min-h-11 shrink-0 cursor-pointer border-b-2 px-4 text-sm font-medium transition-colors',
              tab === item.id
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text)]',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div ref={contentRef}>
        {tab === 'overview' && <OverviewTab site={site} scannerDa={scannerDa} />}
        {tab === 'cookies' && <CookiesTab site={site} onSaved={load} />}
        {tab === 'banner' && <BannerTab site={site} onSaved={load} />}
        {tab === 'embed' && <EmbedTab site={site} snippet={snippet} onSaved={load} />}
        {tab === 'settings' && <SettingsTab site={site} onSaved={load} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Übersicht

function OverviewTab({ site, scannerDa }: { site: SiteDetail; scannerDa: boolean }) {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [scans, setScans] = useState<ScanRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [statsData, scanData] = await Promise.all([
      api.stats(site.id).catch(() => null),
      api.scans(site.id).catch(() => ({ scans: [] as ScanRow[] })),
    ]);
    setStats(statsData);
    setScans(scanData.scans);
  }, [site.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function startScan() {
    setBusy(true);
    setMessage(null);
    try {
      await api.startScan(site.id);
      setMessage('Scan gestartet. Das dauert je nach Seitenzahl ein bis fuenf Minuten.');
      window.setTimeout(() => void load(), 5000);
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : 'Scan konnte nicht gestartet werden.');
    } finally {
      setBusy(false);
    }
  }

  const totals = stats?.totals;
  const rate = totals && totals.total > 0 ? Math.round(((totals.acceptAll + totals.acceptSelection) / totals.total) * 100) : null;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Entscheidungen (30 T.)" value={totals ? String(totals.total) : '0'} />
        <StatTile
          label="Zustimmungsrate"
          value={rate === null ? '-' : `${rate} %`}
          sub="Alle oder Auswahl akzeptiert"
          tone={rate !== null && rate >= 60 ? 'good' : 'neutral'}
        />
        <StatTile
          label="Statistik erlaubt"
          value={totals && totals.total ? `${Math.round((totals.statisticsGranted / totals.total) * 100)} %` : '-'}
        />
        <StatTile
          label="Marketing erlaubt"
          value={totals && totals.total ? `${Math.round((totals.marketingGranted / totals.total) * 100)} %` : '-'}
        />
      </div>

      {totals && totals.total === 0 && (
        <Alert tone="info">
          Noch keine Entscheidungen erfasst. Das ist normal, solange das Script noch nicht auf der Website eingebunden ist.
        </Alert>
      )}

      <Card>
        <SectionTitle
          title="Cookie-Scan"
          hint="Der Scanner öffnet die Website in einem echten Browser, klickt ein vorhandenes Banner weg und protokolliert, was tatsaechlich gesetzt wird."
          action={
            scannerDa ? (
              <Button onClick={startScan} loading={busy} size="sm">
                Scan starten
              </Button>
            ) : undefined
          }
        />
        {!scannerDa && (
          <Alert tone="info">
            Für diese Instanz ist kein Scanner konfiguriert. Die Cookie-Liste bleibt beim aktuellen Stand und muss von
            Hand gepflegt werden. Sobald ein Scanner angebunden ist, erscheint hier der Knopf und der Turnus aus den
            Einstellungen greift.
          </Alert>
        )}
        {message && <Alert tone="info">{message}</Alert>}

        {scans.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">Noch kein Scan durchgeführt.</p>
        ) : (
          <ul className="divide-y divide-[var(--border)]">
            {scans.slice(0, 5).map((scan) => (
              <li key={scan.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                <div>
                  <p className="tabular font-medium">{new Date(scan.startedAt).toLocaleString('de-DE')}</p>
                  <p className="text-xs text-[var(--text-muted)]">
                    {scan.status === 'completed'
                      ? `${scan.pagesScanned.length} Seiten, ${scan.foundCount} Einträge, ${scan.newCount} neu`
                      : scan.status === 'failed'
                        ? (scan.error ?? 'Fehlgeschlagen')
                        : 'läuft'}
                  </p>
                </div>
                <span
                  className={cx(
                    'rounded-full px-2.5 py-1 text-xs font-semibold',
                    scan.status === 'completed'
                      ? 'bg-ok-100 text-ok-500'
                      : scan.status === 'failed'
                        ? 'bg-danger-100 text-danger-500'
                        : 'bg-warn-100 text-warn-500',
                  )}
                >
                  {scan.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- Cookies

function CookiesTab({ site, onSaved }: { site: SiteDetail; onSaved: () => Promise<void> }) {
  const siteId = site.id;
  const [rows, setRows] = useState<CookieRow[] | null>(null);
  const [filter, setFilter] = useState<'all' | CookieCategory>('all');
  const listRef = useStagger<HTMLDivElement>('[data-row]', [rows?.length, filter]);

  const load = useCallback(async () => {
    const data = await api.cookies(siteId).catch(() => ({ cookies: [] as CookieRow[] }));
    setRows(data.cookies);
  }, [siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function change(row: CookieRow, category: CookieCategory) {
    setRows((current) => current?.map((item) => (item.id === row.id ? { ...item, category, reviewed: true } : item)) ?? null);
    await api.updateCookie(row.id, { category }).catch(() => void load());
  }

  if (!rows)
    return (
      <div className="flex justify-center py-12 text-[var(--text-muted)]">
        <Spinner />
      </div>
    );

  if (!rows.length)
    return (
      <EmptyState
        title="Noch keine Cookies erfasst"
        body="Starte einen Scan in der Übersicht. Danach stehen hier alle gefundenen Cookies mit Vorschlag zur Einordnung."
      />
    );

  const unclassified = rows.filter((row) => row.category === 'unclassified').length;
  const filtered = filter === 'all' ? rows : rows.filter((row) => row.category === filter);

  /**
   * Fremddomains, die Cookies setzen, für die es aber keine Blocking-Regel gibt.
   *
   * Das ist der gefaehrlichste stille Zustand: der Eintrag steht brav in der
   * Cookie-Erklärung, geladen wird der Dienst trotzdem vor jeder Einwilligung.
   * Gefunden bei einem echten Scan, dort war es ein eingebetteter
   * Fensterkonfigurator auf der Seite eines Dachdeckers.
   */
  const ungeblockt = [
    ...new Set(
      rows
        .filter((row) => row.thirdParty)
        .map((row) => row.domain.replace(/^\./, ''))
        .filter((host) => !site.blockRules.some((rule) => host.includes(rule.pattern) || rule.pattern.includes(host))),
    ),
  ];

  return (
    <div className="flex flex-col gap-4">
      {unclassified > 0 && (
        <Alert tone="warn">
          {unclassified} {unclassified === 1 ? 'Eintrag ist' : 'Einträge sind'} noch nicht eingeordnet. Ungeklärte Einträge
          erscheinen nicht in der Cookie-Erklärung und werden nicht blockiert.
        </Alert>
      )}

      {ungeblockt.length > 0 && <UnblockedHosts site={site} hosts={ungeblockt} onSaved={onSaved} />}

      <div className="flex gap-2 overflow-x-auto pb-1">
        {(['all', ...CONSENT_CATEGORIES, 'unclassified'] as const).map((key) => (
          <button
            key={key}
            onClick={() => setFilter(key as 'all' | CookieCategory)}
            className={cx(
              'min-h-10 shrink-0 cursor-pointer rounded-full border px-3.5 text-sm font-medium transition-colors',
              filter === key ? 'border-brand-600 bg-brand-600 text-white' : 'border-[var(--border)] text-[var(--text-muted)]',
            )}
          >
            {key === 'all' ? `Alle (${rows.length})` : `${CATEGORY_LABELS[key]} (${rows.filter((r) => r.category === key).length})`}
          </button>
        ))}
      </div>

      {/* Mobile: Karten. Ab lg: Tabelle. Horizontales Scrollen auf dem Handy waere hier unbrauchbar. */}
      <div ref={listRef} className="flex flex-col gap-3">
        {filtered.map((row) => (
          <div
            data-row
            key={row.id}
            className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 lg:grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto_190px] lg:items-center lg:gap-4"
          >
            <div className="min-w-0">
              <p className="tabular truncate font-medium">{row.name}</p>
              <p className="truncate text-xs text-[var(--text-muted)]">
                {row.vendor ?? row.domain}
                {row.thirdParty && ' · Drittanbieter'}
              </p>
            </div>

            <p className="mt-2 line-clamp-2 text-sm text-[var(--text-muted)] lg:mt-0">{row.purpose}</p>

            <div className="mt-2 flex items-center gap-2 lg:mt-0">
              <span className="tabular text-xs text-[var(--text-muted)]">{formatLifetime(row.maxAgeSeconds, 'de')}</span>
              {row.confidence < 60 && !row.reviewed && <CategoryBadge category="unclassified" label="prüfen" />}
            </div>

            <div className="mt-3 lg:mt-0">
              <label className="sr-only" htmlFor={`cat-${row.id}`}>
                Kategorie für {row.name}
              </label>
              <select
                id={`cat-${row.id}`}
                value={row.category}
                onChange={(event) => void change(row, event.target.value as CookieCategory)}
                className="min-h-11 w-full cursor-pointer rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3 text-sm"
              >
                {['necessary', 'preferences', 'statistics', 'marketing', 'unclassified'].map((value) => (
                  <option key={value} value={value}>
                    {CATEGORY_LABELS[value]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Vorschlag, eine Fremddomain zu blockieren.
 *
 * Bewusst nicht automatisch: eine unbekannte Domain blind zu blockieren kann
 * die Kundenseite zerlegen, etwa wenn dort ein Konfigurator oder eine
 * Terminbuchung haengt. Der Mensch entscheidet, das System zeigt nur, was es
 * gesehen hat.
 */
function UnblockedHosts({
  site,
  hosts,
  onSaved,
}: {
  site: SiteDetail;
  hosts: string[];
  onSaved: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  async function block(host: string, category: CookieCategory) {
    if (category === 'unclassified') return;
    setBusy(host);
    try {
      await api.updateSite(site.id, {
        blockRules: [...site.blockRules, { pattern: host, category }],
      });
      await onSaved();
    } finally {
      setBusy(null);
    }
  }

  return (
    <Alert tone="warn">
      <p className="font-semibold">
        {hosts.length === 1
          ? 'Eine Fremddomain setzt Cookies, wird aber nicht blockiert'
          : `${hosts.length} Fremddomains setzen Cookies, werden aber nicht blockiert`}
      </p>
      <p className="mt-1 text-xs">
        Diese Dienste laden vor jeder Einwilligung. In der Cookie-Erklärung stehen sie, blockiert werden sie nicht.
      </p>
      <ul className="mt-3 flex flex-col gap-2">
        {hosts.map((host) => (
          <li key={host} className="flex flex-wrap items-center gap-2">
            <span className="tabular text-sm font-medium">{host}</span>
            <span className="text-xs">blockieren als</span>
            {(['preferences', 'statistics', 'marketing'] as const).map((category) => (
              <button
                key={category}
                type="button"
                disabled={busy === host}
                onClick={() => void block(host, category)}
                className="min-h-9 cursor-pointer rounded-lg border border-current px-2.5 text-xs font-semibold disabled:opacity-50"
              >
                {CATEGORY_LABELS[category]}
              </button>
            ))}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

// ---------------------------------------------------------------- Banner

function BannerTab({ site, onSaved }: { site: SiteDetail; onSaved: () => Promise<void> }) {
  const [config, setConfig] = useState(site.bannerConfig);
  const [texts, setTexts] = useState(site.texts);
  const [lang, setLang] = useState(site.defaultLanguage);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const current = texts[lang];

  async function save() {
    setBusy(true);
    setSaved(false);
    try {
      await api.updateSite(site.id, { bannerConfig: config, texts });
      await onSaved();
      setSaved(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,360px)]">
      <div className="flex flex-col gap-6">
        <Card>
          <SectionTitle title="Darstellung" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Layout"
              value={config.layout}
              onChange={(event) => setConfig({ ...config, layout: event.target.value as typeof config.layout })}
            >
              <option value="box_bottom_left">Box unten links</option>
              <option value="box_bottom_right">Box unten rechts</option>
              <option value="bar_bottom">Leiste unten</option>
              <option value="bar_top">Leiste oben</option>
              <option value="dialog_center">Dialog mittig</option>
            </Select>
            <Select
              label="Farbschema"
              value={config.theme}
              onChange={(event) => setConfig({ ...config, theme: event.target.value as typeof config.theme })}
            >
              <option value="light">Hell</option>
              <option value="dark">Dunkel</option>
              <option value="auto">Automatisch</option>
            </Select>
            <Field
              label="Primärfarbe"
              type="color"
              className="h-11 p-1"
              value={config.colors.primary}
              onChange={(event) => setConfig({ ...config, colors: { ...config.colors, primary: event.target.value } })}
            />
            <Field
              label="Eckenradius"
              type="number"
              min={0}
              max={28}
              value={config.cornerRadius}
              onChange={(event) => setConfig({ ...config, cornerRadius: Number(event.target.value) })}
            />
            <Select
              label="Einstellungen-Button"
              value={config.badge}
              onChange={(event) => setConfig({ ...config, badge: event.target.value as typeof config.badge })}
            >
              <option value="bottom_left">unten links</option>
              <option value="bottom_right">unten rechts</option>
              <option value="top_left">oben links</option>
              <option value="top_right">oben rechts</option>
              <option value="hidden">ausblenden</option>
            </Select>
            {config.badge !== 'hidden' && (
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Abstand seitlich"
                  type="number"
                  min={0}
                  max={200}
                  value={config.badgeOffsetX ?? 16}
                  onChange={(event) => setConfig({ ...config, badgeOffsetX: Number(event.target.value) })}
                  hint="Pixel"
                />
                <Field
                  label="Abstand oben/unten"
                  type="number"
                  min={0}
                  max={200}
                  value={config.badgeOffsetY ?? 16}
                  onChange={(event) => setConfig({ ...config, badgeOffsetY: Number(event.target.value) })}
                  hint="Pixel"
                />
              </div>
            )}
          </div>

          <div className="mt-4 divide-y divide-[var(--border)]">
            <Toggle
              label="Ablehnen-Button anzeigen"
              description="In der EU erwarten Aufsichtsbehoerden eine gleichwertige Ablehnen-Option auf der ersten Ebene."
              checked={config.showRejectButton}
              onChange={(value) => setConfig({ ...config, showRejectButton: value })}
            />
            <Toggle
              label="Annehmen und Ablehnen gleich gestalten"
              description="Empfohlen. Ein blasser oder kleinerer Ablehnen-Button gilt als unzulaessige Beeinflussung (EDPB Cookie Banner Taskforce 2023, DSK)."
              checked={config.equalChoiceStyling !== false}
              onChange={(value) => setConfig({ ...config, equalChoiceStyling: value })}
            />
            <Toggle
              label="Seite abdunkeln"
              description="Blendet den Hintergrund aus, bis eine Entscheidung getroffen wurde."
              checked={config.blocking}
              onChange={(value) => setConfig({ ...config, blocking: value })}
            />
            <Toggle
              label="Animationen"
              checked={config.animations}
              onChange={(value) => setConfig({ ...config, animations: value })}
            />
            <Toggle
              label="Herkunftsangabe anzeigen"
              description="Maskottchen und Name unten im Banner, verlinkt auf ilovecookies. Im freien Tarif Teil der Gegenleistung."
              checked={config.showBranding !== false}
              onChange={(value) => setConfig({ ...config, showBranding: value })}
            />
          </div>
        </Card>

        <Card>
          <SectionTitle
            title="Texte"
            action={
              <Select label="" aria-label="Sprache" value={lang} onChange={(event) => setLang(event.target.value)}>
                {Object.keys(texts).map((key) => (
                  <option key={key} value={key}>
                    {key.toUpperCase()}
                  </option>
                ))}
              </Select>
            }
          />
          {current && (
            <div className="flex flex-col gap-4">
              <Field
                label="Überschrift"
                value={current.title}
                onChange={(event) => setTexts({ ...texts, [lang]: { ...current, title: event.target.value } })}
              />
              <div className="flex flex-col gap-1.5">
                <label htmlFor="body-text" className="text-sm font-medium">
                  Text
                </label>
                <textarea
                  id="body-text"
                  rows={4}
                  value={current.body}
                  onChange={(event) => setTexts({ ...texts, [lang]: { ...current, body: event.target.value } })}
                  className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3 text-[15px] outline-none focus:border-brand-500"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field
                  label="Alle akzeptieren"
                  value={current.acceptAll}
                  onChange={(event) => setTexts({ ...texts, [lang]: { ...current, acceptAll: event.target.value } })}
                />
                <Field
                  label="Ablehnen"
                  value={current.rejectAll}
                  onChange={(event) => setTexts({ ...texts, [lang]: { ...current, rejectAll: event.target.value } })}
                />
                <Field
                  label="Einstellungen"
                  value={current.settings}
                  onChange={(event) => setTexts({ ...texts, [lang]: { ...current, settings: event.target.value } })}
                />
              </div>
            </div>
          )}
        </Card>

        {config.equalChoiceStyling === false && (
          <Alert tone="warn">
            Ablehnen ist optisch schwächer als Annehmen. Das ist der häufigste Beanstandungsgrund bei Cookie-Bannern in
            der EU. Nur bewusst so lassen.
          </Alert>
        )}

        <div className="flex items-center gap-3">
          <Button onClick={save} loading={busy}>
            Speichern
          </Button>
          {saved && <span className="text-sm text-ok-500">Gespeichert. Änderungen sind nach spätestens 5 Minuten live.</span>}
        </div>
      </div>

      <BannerPreview config={config} texts={current} />
    </div>
  );
}

/** Vorschau im selben Layout wie das echte Banner, nur ohne Shadow DOM. */
function BannerPreview({ config, texts }: { config: SiteDetail['bannerConfig']; texts?: SiteDetail['texts'][string] }) {
  if (!texts) return null;
  return (
    <div className="lg:sticky lg:top-6 lg:self-start">
      <p className="mb-2 text-sm font-medium text-[var(--text-muted)]">Vorschau</p>
      <div className="relative overflow-hidden rounded-[var(--radius-card)] border border-[var(--border)] bg-[repeating-linear-gradient(45deg,#f8fafc,#f8fafc_10px,#f1f5f9_10px,#f1f5f9_20px)] p-4">
        {/* Der Wiederaufruf-Button an der gewaehlten Ecke. Der Abstand ist
            verkleinert dargestellt, die Ecke stimmt. */}
        {config.badge !== 'hidden' && (
          <span
            className="absolute grid size-9 place-items-center rounded-full border bg-white shadow-md"
            style={{
              borderColor: config.colors.border,
              color: config.colors.primary,
              [config.badge.endsWith('_left') ? 'left' : 'right']: Math.min((config.badgeOffsetX ?? 16) / 3, 26) + 4,
              [config.badge.startsWith('top_') ? 'top' : 'bottom']: Math.min((config.badgeOffsetY ?? 16) / 3, 26) + 4,
            }}
            title="Einstellungen erneut öffnen"
          >
            <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5Z" strokeLinejoin="round" />
              <circle cx="9" cy="10" r="1" fill="currentColor" stroke="none" />
              <circle cx="14" cy="15" r="1" fill="currentColor" stroke="none" />
              <circle cx="9.5" cy="15.5" r="1" fill="currentColor" stroke="none" />
            </svg>
          </span>
        )}
        <div
          style={{
            background: config.colors.background,
            color: config.colors.foreground,
            borderRadius: config.cornerRadius,
            borderColor: config.colors.border,
          }}
          className="border p-4 shadow-lg"
        >
          <p className="text-[15px] font-semibold">{texts.title}</p>
          <p className="mt-1.5 text-xs leading-relaxed opacity-80">{texts.body}</p>
          <div className="mt-3 flex flex-col gap-2">
            <span
              style={{ background: config.colors.primary, color: config.colors.onPrimary, borderRadius: Math.max(0, config.cornerRadius - 4) }}
              className="px-3 py-2.5 text-center text-sm font-semibold"
            >
              {texts.acceptAll}
            </span>
            {config.showRejectButton && (
              <span
                style={
                  config.equalChoiceStyling !== false
                    ? {
                        background: config.colors.primary,
                        color: config.colors.onPrimary,
                        borderRadius: Math.max(0, config.cornerRadius - 4),
                      }
                    : { background: config.colors.secondary, borderRadius: Math.max(0, config.cornerRadius - 4) }
                }
                className="px-3 py-2.5 text-center text-sm font-semibold"
              >
                {texts.rejectAll}
              </span>
            )}
            <span className="px-3 py-2 text-center text-sm font-medium underline opacity-80">{texts.settings}</span>
          </div>

          {config.showBranding !== false && texts.poweredBy && (
            <div
              className="mt-3 flex items-center justify-end gap-1.5 border-t pt-2.5 text-xs font-semibold opacity-85"
              style={{ borderColor: config.colors.border }}
            >
              {/* Dieselbe Darstellung wie im echten Banner: 24 Pixel, rechts unten. */}
              <img src="/brand/crumblemonster-96.webp" alt="" width={20} height={20} className="size-5" />
              <span>{texts.poweredBy}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

